"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { getGlobalRotation } from "@/utils/globalState";
import { useBreakpoint } from "@/utils/useBreakpoint";

const ScrollHelpers = () => {
  const [showScroll, setShowScroll] = useState(true);
  const [rotation, setRotation] = useState(0);
  const [isSnapping, setIsSnapping] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";

  useEffect(() => {
    const handleScroll = () => {
      const bottomThreshold = 500;
      const isNearBottom =
        window.innerHeight + window.pageYOffset >=
        document.documentElement.scrollHeight - bottomThreshold;

      setShowScroll(!isNearBottom);
    };

    const checkRotation = () => {
      setRotation(window.scrollY === 0 ? 0 : getGlobalRotation());
    };

    const handleMobileScroll = () => {
      if (!isMobile || isSnapping) return;

      const currentScrollY = window.scrollY;
      const scrollDirection =
        currentScrollY > lastScrollY || currentScrollY === 0 ? "down" : "up";
      const scrollDelta = Math.abs(currentScrollY - lastScrollY);

      if (scrollDelta > 5) {
        setIsSnapping(true);

        const sections = document.querySelectorAll(
          "section[data-scroll-position]"
        );
        let targetScrollY = 0;

        if (scrollDirection === "down") {
          for (let i = 0; i < sections.length; i++) {
            const sectionTop =
              sections[i].getBoundingClientRect().top + window.scrollY;
            if (sectionTop > currentScrollY + 20) {
              targetScrollY = sectionTop;
              break;
            }
          }
        } else {
          for (let i = sections.length - 1; i >= 0; i--) {
            const sectionTop =
              sections[i].getBoundingClientRect().top + window.scrollY;
            if (sectionTop < currentScrollY - 20) {
              targetScrollY = sectionTop;
              break;
            }
          }
        }

        if (targetScrollY > 0) {
          window.scrollTo({
            top: targetScrollY,
            behavior: "smooth",
          });

          setTimeout(() => {
            setIsSnapping(false);
          }, 800);
        } else {
          setIsSnapping(false);
        }
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    const rotationInterval = setInterval(checkRotation, 100);

    if (isMobile) {
      window.addEventListener("scroll", handleMobileScroll);
    }

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (isMobile) {
        window.removeEventListener("scroll", handleMobileScroll);
      }
      clearInterval(rotationInterval);
    };
  }, [isMobile, isSnapping, lastScrollY]);

  useEffect(() => {
    const preventScrollWhileSnapping = (e) => {
      if (isMobile && isSnapping) {
        e.preventDefault();
      }
    };

    window.addEventListener("wheel", preventScrollWhileSnapping, {
      passive: false,
    });
    window.addEventListener("touchmove", preventScrollWhileSnapping, {
      passive: false,
    });

    return () => {
      window.removeEventListener("wheel", preventScrollWhileSnapping);
      window.removeEventListener("touchmove", preventScrollWhileSnapping);
    };
  }, [isMobile, isSnapping]);

  if (!showScroll) return null;

  return (
    <>
      {/* Scroll icon mobile*/}
      {/* <div className="fixed right-5 top-1/2 transform -translate-y-1/2 xl:hidden" style={{ zIndex: 9999999 }}>
      <svg width="11" height="96" viewBox="0 0 11 96" fill="#193762" xmlns="http://www.w3.org/2000/svg">
<path d="M2.904 2.144C2.904 1.328 3.28 0.784 3.736 0.48C3.8 0.44 3.848 0.44 3.912 0.496L4.12 0.68C4.192 0.744 4.168 0.816 4.112 0.864C3.728 1.16 3.456 1.536 3.456 2.144C3.456 2.864 3.84 3.288 4.424 3.288C5.04 3.288 5.288 2.84 5.568 2.272L5.896 1.592C6.176 1 6.6 0.608 7.264 0.608C8.128 0.608 8.664 1.288 8.664 2.16C8.664 2.8 8.392 3.288 7.952 3.648C7.872 3.72 7.824 3.704 7.76 3.648L7.56 3.472C7.488 3.408 7.496 3.344 7.56 3.28C7.92 2.968 8.112 2.64 8.112 2.176C8.112 1.624 7.824 1.192 7.272 1.192C6.864 1.192 6.6 1.424 6.36 1.92L6.032 2.584C5.712 3.24 5.328 3.872 4.432 3.872C3.48 3.872 2.904 3.144 2.904 2.144ZM2.896 7.7605C2.896 6.1045 4.16 4.9205 5.776 4.9205C7.392 4.9205 8.664 6.1045 8.664 7.7605C8.664 8.5365 8.384 9.1525 7.944 9.6485C7.888 9.7045 7.832 9.7365 7.752 9.6725L7.504 9.4485C7.448 9.3925 7.448 9.3445 7.504 9.2725C7.864 8.8645 8.08 8.3765 8.08 7.7765C8.08 6.4885 7.104 5.5285 5.776 5.5285C4.448 5.5285 3.48 6.4885 3.48 7.7765C3.48 8.3765 3.696 8.8645 4.056 9.2725C4.12 9.3445 4.112 9.3925 4.064 9.4405L3.808 9.6725C3.736 9.7445 3.68 9.7045 3.624 9.6485C3.176 9.1525 2.896 8.5365 2.896 7.7605ZM3 13.9572C3 13.8932 3.024 13.8452 3.08 13.8132L5.12 12.4612L5.12 11.5652L3.112 11.5652C3.04 11.5652 3 11.5252 3 11.4532L3 11.0772C3 11.0052 3.04 10.9652 3.112 10.9652L8.488 10.9652C8.56 10.9652 8.6 11.0052 8.6 11.0772L8.6 12.6052C8.6 13.8132 7.872 14.4452 6.856 14.4452C5.992 14.4452 5.328 13.9812 5.16 13.0772L3.144 14.4372C3.056 14.4932 3 14.4532 3 14.3572L3 13.9572ZM5.656 11.5652L5.656 12.5572C5.656 13.2852 5.976 13.8612 6.856 13.8612C7.744 13.8612 8.064 13.2852 8.064 12.5572L8.064 11.5652L5.656 11.5652ZM2.896 18.2257C2.896 16.6177 4.16 15.3617 5.776 15.3617C7.4 15.3617 8.664 16.6177 8.664 18.2257C8.664 19.8337 7.4 21.0898 5.776 21.0898C4.16 21.0897 2.896 19.8337 2.896 18.2257ZM5.776 15.9697C4.464 15.9697 3.48 16.9377 3.48 18.2257C3.48 19.5057 4.464 20.4817 5.776 20.4817C7.096 20.4817 8.08 19.5058 8.08 18.2257C8.08 16.9377 7.096 15.9697 5.776 15.9697ZM3 22.4872C3 22.4152 3.04 22.3752 3.112 22.3752L8.488 22.3752C8.56 22.3752 8.6 22.4152 8.6 22.4872L8.6 22.8632C8.6 22.9352 8.56 22.9752 8.488 22.9752L3.536 22.9752L3.536 25.0072C3.536 25.0792 3.488 25.1272 3.416 25.1272L3.12 25.1272C3.048 25.1272 3 25.0792 3 25.0072L3 22.4872ZM3 26.45C3 26.378 3.04 26.338 3.112 26.338L8.488 26.338C8.56 26.338 8.6 26.378 8.6 26.45L8.6 26.826C8.6 26.898 8.56 26.938 8.488 26.938L3.536 26.938L3.536 28.97C3.536 29.042 3.488 29.09 3.416 29.09L3.12 29.09C3.048 29.09 3 29.042 3 28.97L3 26.45Z" fill="#193762"/>
<path d="M9.5 92.124L6.04932 95.2483L2.59863 92.124" stroke="#193762" stroke-linecap="round"/>
<line x1="5.5" y1="33" x2="5.5" y2="95" stroke="#193762"/>
<circle cx="3.5" cy="3.5" r="3.5" transform="matrix(-1 -8.74228e-08 -8.74228e-08 1 9 46)" fill="#193762"/>
</svg>

      </div> */}

      <div className="hidden flex fixed right-5 top-[10rem] transform -translate-y-1/2 flex-col gap-3 xl:hidden" style={{ zIndex: 999999 }}>
        <button className="p-3 rounded bg-white/30 transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" opacity="0.5">
            <path d="M12 19.5L12 4.5M12 4.5L5 11.5M12 4.5L19 11.5" stroke="#193762" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button className="p-3 rounded bg-white/30 transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" opacity="0.5">
            <path d="M12 4.5L12 19.5M12 19.5L5 12.5M12 19.5L19 12.5" stroke="#193762" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      <motion.div
        className="hidden xl:block fixed top-[47vh] right-6"
        style={{ zIndex: 999999 }}
        animate={{ rotate: rotation }}
        transition={{ duration: 0.5 }}
      >
        <div className="bg-white/10 backdrop-blur-sm p-4 rounded">
          {rotation != 90 && rotation != 180 ? (
            <svg
              width="29"
              height="88"
              viewBox="0 0 29 88"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 84.1248L5.04932 87.249L1.59863 84.1248"
                stroke="#193762"
                strokeLinecap="round"
              />
              <line
                x1="5"
                y1="0.000488347"
                x2="4.99999"
                y2="87.0005"
                stroke="#193762"
              />
              <circle
                cx="4.5"
                cy="4.5"
                r="4.5"
                transform="matrix(-1 -8.74228e-08 -8.74228e-08 1 9 30.0005)"
                fill="#193762"
              />
              <path
                d="M17.368 30.375C17.368 29.253 17.885 28.505 18.512 28.087C18.6 28.032 18.666 28.032 18.754 28.109L19.04 28.362C19.139 28.45 19.106 28.549 19.029 28.615C18.501 29.022 18.127 29.539 18.127 30.375C18.127 31.365 18.655 31.948 19.458 31.948C20.305 31.948 20.646 31.332 21.031 30.551L21.482 29.616C21.867 28.802 22.45 28.263 23.363 28.263C24.551 28.263 25.288 29.198 25.288 30.397C25.288 31.277 24.914 31.948 24.309 32.443C24.199 32.542 24.133 32.52 24.045 32.443L23.77 32.201C23.671 32.113 23.682 32.025 23.77 31.937C24.265 31.508 24.529 31.057 24.529 30.419C24.529 29.66 24.133 29.066 23.374 29.066C22.813 29.066 22.45 29.385 22.12 30.067L21.669 30.98C21.229 31.882 20.701 32.751 19.469 32.751C18.16 32.751 17.368 31.75 17.368 30.375Z M17.357 38.0976C17.357 35.8206 19.095 34.1926 21.317 34.1926C23.539 34.1926 25.288 35.8206 25.288 38.0976C25.288 39.1646 24.903 40.0116 24.298 40.6936C24.221 40.7706 24.144 40.8146 24.034 40.7266L23.693 40.4186C23.616 40.3416 23.616 40.2756 23.693 40.1766C24.188 39.6156 24.485 38.9446 24.485 38.1196C24.485 36.3486 23.143 35.0286 21.317 35.0286C19.491 35.0286 18.16 36.3486 18.16 38.1196C18.16 38.9446 18.457 39.6156 18.952 40.1766C19.04 40.2756 19.029 40.3416 18.963 40.4076L18.611 40.7266C18.512 40.8256 18.435 40.7706 18.358 40.6936C17.742 40.0116 17.357 39.1646 17.357 38.0976Z M17.5 46.6181C17.5 46.5301 17.533 46.4641 17.61 46.4201L20.415 44.5611L20.415 43.3291L17.654 43.3291C17.555 43.3291 17.5 43.2741 17.5 43.1751L17.5 42.6581C17.5 42.5591 17.555 42.5041 17.654 42.5041L25.046 42.5041C25.145 42.5041 25.2 42.5591 25.2 42.6581L25.2 44.7591C25.2 46.4201 24.199 47.2891 22.802 47.2891C21.614 47.2891 20.701 46.6511 20.47 45.4081L17.698 47.2781C17.577 47.3551 17.5 47.3001 17.5 47.1681L17.5 46.6181ZM21.152 43.3291L21.152 44.6931C21.152 45.6941 21.592 46.4861 22.802 46.4861C24.023 46.4861 24.463 45.6941 24.463 44.6931L24.463 43.3291L21.152 43.3291Z M17.357 52.4874C17.357 50.2764 19.095 48.5494 21.317 48.5494C23.55 48.5494 25.288 50.2764 25.288 52.4874C25.288 54.6984 23.55 56.4254 21.317 56.4254C19.095 56.4254 17.357 54.6984 17.357 52.4874ZM21.317 49.3854C19.513 49.3854 18.16 50.7164 18.16 52.4874C18.16 54.2474 19.513 55.5894 21.317 55.5894C23.132 55.5894 24.485 54.2474 24.485 52.4874C24.485 50.7164 23.132 49.3854 21.317 49.3854Z M17.5 58.3468C17.5 58.2478 17.555 58.1928 17.654 58.1928L25.046 58.1928C25.145 58.1928 25.2 58.2478 25.2 58.3468L25.2 58.8638C25.2 58.9628 25.145 59.0178 25.046 59.0178L18.237 59.0178L18.237 61.8118C18.237 61.9108 18.171 61.9768 18.072 61.9768L17.665 61.9768C17.566 61.9768 17.5 61.9108 17.5 61.8118L17.5 58.3468Z M17.5 63.7957C17.5 63.6967 17.555 63.6417 17.654 63.6417L25.046 63.6417C25.145 63.6417 25.2 63.6967 25.2 63.7957L25.2 64.3127C25.2 64.4117 25.145 64.4667 25.046 64.4667L18.237 64.4667L18.237 67.2607C18.237 67.3597 18.171 67.4257 18.072 67.4257L17.665 67.4257C17.566 67.4257 17.5 67.3597 17.5 67.2607L17.5 63.7957Z"
                fill="#193762"
                transform="scale(1.2) translate(-3, -6)"
              />
            </svg>
          ) : (
            <svg
              width="30"
              height="89"
              viewBox="0 0 30 89"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.25 84.375L5.79932 87.4993L2.34863 84.375"
                stroke="#193762"
                strokeLinecap="round"
              />
              <line
                x1="5.75"
                y1="0.250488"
                x2="5.74999"
                y2="87.2505"
                stroke="#193762"
              />
              <circle
                cx="4.5"
                cy="4.5"
                r="4.5"
                transform="matrix(-1 -8.74228e-08 -8.74228e-08 1 9.75 30.2505)"
                fill="#193762"
              />
              <path
                d="M25.382 57.875C25.382 58.997 24.865 59.745 24.238 60.163C24.15 60.218 24.084 60.218 23.996 60.141L23.71 59.888C23.611 59.8 23.644 59.701 23.721 59.635C24.249 59.228 24.623 58.711 24.623 57.875C24.623 56.885 24.095 56.302 23.292 56.302C22.445 56.302 22.104 56.918 21.719 57.699L21.268 58.634C20.883 59.448 20.3 59.987 19.387 59.987C18.199 59.987 17.462 59.052 17.462 57.853C17.462 56.973 17.836 56.302 18.441 55.807C18.551 55.708 18.617 55.73 18.705 55.807L18.98 56.049C19.079 56.137 19.068 56.225 18.98 56.313C18.485 56.742 18.221 57.193 18.221 57.831C18.221 58.59 18.617 59.184 19.376 59.184C19.937 59.184 20.3 58.865 20.63 58.183L21.081 57.27C21.521 56.368 22.049 55.499 23.281 55.499C24.59 55.499 25.382 56.5 25.382 57.875Z M25.393 50.1524C25.393 52.4294 23.655 54.0574 21.433 54.0574C19.211 54.0574 17.462 52.4294 17.462 50.1524C17.462 49.0854 17.847 48.2384 18.452 47.5564C18.529 47.4794 18.606 47.4354 18.716 47.5234L19.057 47.8314C19.134 47.9084 19.134 47.9744 19.057 48.0734C18.562 48.6344 18.265 49.3054 18.265 50.1304C18.265 51.9014 19.607 53.2214 21.433 53.2214C23.259 53.2214 24.59 51.9014 24.59 50.1304C24.59 49.3054 24.293 48.6344 23.798 48.0734C23.71 47.9744 23.721 47.9084 23.787 47.8424L24.139 47.5234C24.238 47.4244 24.315 47.4794 24.392 47.5564C25.008 48.2384 25.393 49.0854 25.393 50.1524Z M25.25 41.6319C25.25 41.7199 25.217 41.7859 25.14 41.8299L22.335 43.6889L22.335 44.9209H25.096C25.195 44.9209 25.25 44.9759 25.25 45.0749V45.5919C25.25 45.6909 25.195 45.7459 25.096 45.7459H17.704C17.605 45.7459 17.55 45.6909 17.55 45.5919V43.4909C17.55 41.8299 18.551 40.9609 19.948 40.9609C21.136 40.9609 22.049 41.5989 22.28 42.8419L25.052 40.9719C25.173 40.8949 25.25 40.9499 25.25 41.0819V41.6319ZM21.598 44.9209V43.5569C21.598 42.5559 21.158 41.7639 19.948 41.7639C18.727 41.7639 18.287 42.5559 18.287 43.5569V44.9209H21.598Z M25.393 35.7626C25.393 37.9736 23.655 39.7006 21.433 39.7006C19.2 39.7006 17.462 37.9736 17.462 35.7626C17.462 33.5516 19.2 31.8246 21.433 31.8246C23.655 31.8246 25.393 33.5516 25.393 35.7626ZM21.433 38.8646C23.237 38.8646 24.59 37.5336 24.59 35.7626C24.59 34.0026 23.237 32.6606 21.433 32.6606C19.618 32.6606 18.265 34.0026 18.265 35.7626C18.265 37.5336 19.618 38.8646 21.433 38.8646Z M25.25 29.9032C25.25 30.0022 25.195 30.0572 25.096 30.0572H17.704C17.605 30.0572 17.55 30.0022 17.55 29.9032V29.3862C17.55 29.2872 17.605 29.2322 17.704 29.2322H24.513V26.4382C24.513 26.3392 24.579 26.2732 24.678 26.2732H25.085C25.184 26.2732 25.25 26.3392 25.25 26.4382V29.9032Z M25.25 24.4543C25.25 24.5533 25.195 24.6083 25.096 24.6083H17.704C17.605 24.6083 17.55 24.5533 17.55 24.4543V23.9373C17.55 23.8383 17.605 23.7833 17.704 23.7833H24.513V20.9893C24.513 20.8903 24.579 20.8243 24.678 20.8243H25.085C25.184 20.8243 25.25 20.8903 25.25 20.9893V24.4543Z"
                fill="#193762"
                transform="scale(1.2) translate(-3, -10)"
              />
            </svg>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default ScrollHelpers;
